import SEO from '@/components/SEO';
import MarketingHeader from '@/components/layout/MarketingHeader';
import MarketingFooter from '@/components/layout/MarketingFooter';

const TermsOfServicePage = () => {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Terms of Service - Review Plum',
    description: 'Read the Terms of Service for Review Plum. Understand your rights and responsibilities when using our AI-powered Google review generation platform.',
    url: 'https://reviewplum.com/terms-of-service',
    publisher: {
      '@type': 'Organization',
      name: 'Review Plum',
      url: 'https://reviewplum.com',
    },
    dateModified: '2026-01-20',
  };

  return (
    <>
      <SEO
        title="Terms of Service - Review Plum | User Agreement & Policies"
        description="Read Review Plum's Terms of Service. Understand the rules, user responsibilities, and legal agreement for using our AI-powered Google review generation platform."
        keywords={['terms of service', 'user agreement', 'review plum terms', 'legal terms', 'service agreement']}
        canonicalUrl="https://reviewplum.com/terms-of-service"
        jsonLd={jsonLd}
      />
      
      <div className="min-h-screen bg-background">
        <MarketingHeader />
        
        <main className="container mx-auto px-4 py-16 max-w-4xl">
          <article itemScope itemType="https://schema.org/WebPage">
            <header className="mb-12">
              <h1 className="text-4xl font-bold text-foreground mb-4" itemProp="name">
                Terms of Service
              </h1>
              <p className="text-muted-foreground">
                Last updated: January 20, 2026
              </p>
            </header>

            <div className="prose prose-lg max-w-none space-y-8" itemProp="text">
              <section aria-labelledby="acceptance">
                <h2 id="acceptance" className="text-2xl font-semibold text-foreground mb-4">
                  1. Acceptance of Terms
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  Welcome to Review Plum. By accessing or using our AI-powered Google review generation platform ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  These Terms constitute a legally binding agreement between you and Review Plum. We reserve the right to modify these Terms at any time. Continued use after modifications constitutes acceptance of the updated Terms.
                </p>
              </section>

              <section aria-labelledby="eligibility">
                <h2 id="eligibility" className="text-2xl font-semibold text-foreground mb-4">
                  2. Eligibility
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  To use Review Plum, you must:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                  <li>Be at least 18 years of age</li>
                  <li>Have the legal capacity to enter into a binding agreement</li>
                  <li>Be an authorized representative of the business you register</li>
                  <li>Provide accurate and complete registration information</li>
                  <li>Comply with all applicable laws and regulations</li>
                </ul>
              </section>

              <section aria-labelledby="service-description">
                <h2 id="service-description" className="text-2xl font-semibold text-foreground mb-4">
                  3. Service Description
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  Review Plum provides an AI-powered platform that helps businesses generate authentic, Google-compliant review suggestions. Our Service includes:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                  <li>AI-generated review suggestions based on your business profile</li>
                  <li>Customizable review categories and keywords</li>
                  <li>QR codes and shareable links for customer review collection</li>
                  <li>Review analytics and management dashboard</li>
                  <li>Multi-language support for review generation</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  <strong>Important:</strong> Review Plum generates review suggestions that customers may use as inspiration. We do not guarantee that customers will submit reviews or that reviews will be published by Google.
                </p>
              </section>

              <section aria-labelledby="user-responsibilities">
                <h2 id="user-responsibilities" className="text-2xl font-semibold text-foreground mb-4">
                  4. User Responsibilities
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  When using Review Plum, you agree to:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>Provide accurate business information</li>
                  <li>Use the Service only for legitimate business purposes</li>
                  <li>Not manipulate, fabricate, or post fake reviews</li>
                  <li>Comply with Google's review policies and guidelines</li>
                  <li>Ensure customers voluntarily choose to leave reviews</li>
                  <li>Not incentivize customers inappropriately for reviews</li>
                  <li>Maintain the confidentiality of your account credentials</li>
                  <li>Not share your account with unauthorized parties</li>
                </ul>
              </section>

              <section aria-labelledby="prohibited-uses">
                <h2 id="prohibited-uses" className="text-2xl font-semibold text-foreground mb-4">
                  5. Prohibited Uses
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  You may not use Review Plum to:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>Generate or post fake, misleading, or fraudulent reviews</li>
                  <li>Violate Google's Terms of Service or review policies</li>
                  <li>Impersonate another business or individual</li>
                  <li>Harass, abuse, or harm others</li>
                  <li>Distribute malware, viruses, or harmful code</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Scrape, copy, or reproduce our platform content</li>
                  <li>Use automated tools without our written permission</li>
                  <li>Engage in any activity that violates applicable laws</li>
                </ul>
              </section>

              <section aria-labelledby="google-compliance">
                <h2 id="google-compliance" className="text-2xl font-semibold text-foreground mb-4">
                  6. Google Compliance
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  Review Plum is designed to comply with Google's review policies. However, you are ultimately responsible for ensuring that your use of our Service and any reviews submitted comply with Google's Terms of Service and review guidelines.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  We do not guarantee that reviews generated using our platform will be accepted or remain published by Google. Google may remove reviews at its sole discretion.
                </p>
              </section>

              <section aria-labelledby="payment-terms">
                <h2 id="payment-terms" className="text-2xl font-semibold text-foreground mb-4">
                  7. Payment Terms
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  For paid subscriptions:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>All fees are quoted in Indian Rupees (INR) unless otherwise specified</li>
                  <li>Subscription fees are billed in advance on a yearly basis</li>
                  <li>All payments are non-refundable unless otherwise stated</li>
                  <li>We reserve the right to change pricing with 30 days notice</li>
                  <li>You authorize us to charge your payment method for all applicable fees</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  Failure to pay may result in suspension or termination of your account.
                </p>
              </section>

              <section aria-labelledby="intellectual-property">
                <h2 id="intellectual-property" className="text-2xl font-semibold text-foreground mb-4">
                  8. Intellectual Property
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  All content, features, and functionality of Review Plum, including but not limited to text, graphics, logos, icons, software, and AI models, are owned by Review Plum and protected by intellectual property laws.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  You retain ownership of your business information and content you provide. By using our Service, you grant us a limited license to use this information to provide and improve our services.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  Generated review suggestions are provided for your use. You may modify and use them as needed for your business.
                </p>
              </section>

              <section aria-labelledby="disclaimers">
                <h2 id="disclaimers" className="text-2xl font-semibold text-foreground mb-4">
                  9. Disclaimers
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                  <li>The Service will be uninterrupted or error-free</li>
                  <li>Reviews will be published or remain visible on Google</li>
                  <li>Generated reviews will achieve specific business outcomes</li>
                  <li>The Service will meet all your requirements</li>
                </ul>
              </section>

              <section aria-labelledby="limitation-liability">
                <h2 id="limitation-liability" className="text-2xl font-semibold text-foreground mb-4">
                  10. Limitation of Liability
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, REVIEW PLUM SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  Our total liability for any claims arising from your use of the Service shall not exceed the amount you paid to us in the 12 months preceding the claim.
                </p>
              </section>

              <section aria-labelledby="indemnification">
                <h2 id="indemnification" className="text-2xl font-semibold text-foreground mb-4">
                  11. Indemnification
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  You agree to indemnify and hold harmless Review Plum, its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including legal fees) arising from:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                  <li>Your use of the Service</li>
                  <li>Your violation of these Terms</li>
                  <li>Your violation of any third-party rights</li>
                  <li>Any reviews posted using our Service</li>
                </ul>
              </section>

              <section aria-labelledby="termination">
                <h2 id="termination" className="text-2xl font-semibold text-foreground mb-4">
                  12. Termination
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may suspend or terminate your access to the Service at any time, with or without cause, with or without notice. You may also terminate your account at any time through your account settings.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  Upon termination, your right to use the Service will immediately cease. Provisions that by their nature should survive termination will continue to apply.
                </p>
              </section>

              <section aria-labelledby="governing-law">
                <h2 id="governing-law" className="text-2xl font-semibold text-foreground mb-4">
                  13. Governing Law
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising from these Terms or your use of the Service shall be subject to the exclusive jurisdiction of the courts in Bangalore, Karnataka, India.
                </p>
              </section>

              <section aria-labelledby="dispute-resolution">
                <h2 id="dispute-resolution" className="text-2xl font-semibold text-foreground mb-4">
                  14. Dispute Resolution
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  Before initiating any legal proceedings, you agree to first attempt to resolve disputes informally by contacting us at arpit@reviewplum.com. We will attempt to resolve the dispute within 30 days.
                </p>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  If informal resolution fails, disputes may be resolved through binding arbitration in accordance with the Arbitration and Conciliation Act, 1996.
                </p>
              </section>

              <section aria-labelledby="severability">
                <h2 id="severability" className="text-2xl font-semibold text-foreground mb-4">
                  15. Severability
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
                </p>
              </section>

              <section aria-labelledby="entire-agreement">
                <h2 id="entire-agreement" className="text-2xl font-semibold text-foreground mb-4">
                  16. Entire Agreement
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  These Terms, together with our Privacy Policy, constitute the entire agreement between you and Review Plum regarding the use of our Service and supersede all prior agreements and understandings.
                </p>
              </section>

              <section aria-labelledby="contact-terms">
                <h2 id="contact-terms" className="text-2xl font-semibold text-foreground mb-4">
                  17. Contact Information
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  For questions about these Terms of Service, please contact us:
                </p>
                <div className="mt-4 p-6 bg-muted/50 rounded-lg">
                  <p className="text-foreground font-medium">Review Plum</p>
                  <p className="text-muted-foreground">Email: arpit@reviewplum.com</p>
                  <p className="text-muted-foreground">Website: https://reviewplum.com</p>
                </div>
              </section>
            </div>
          </article>
        </main>
        
        <MarketingFooter />
      </div>
    </>
  );
};

export default TermsOfServicePage;
